import { ordersService } from '@/services/ordersService';
import { entityService } from '@/services/EntityService';
import { formatNomenclatureWithSize } from '@equip/utils';
import { outputReport, getOutputFormat, getPreOpenedWindow } from '@/utils/reportOutput';
import {
  buildAssetAcquisitionOrderHtml,
  type AssetAcquisitionOrderItem,
  type AssetAcquisitionOrderTemplateOptions,
} from '@/reports/templates/assetAcquisitionOrderTemplate';
import type { Entity } from '@/types/Entity';

const normalizeStringParam = (value: unknown): string | null => {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const isUuid = (value: string): boolean =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);

const normalizeOrderNumber = (value: unknown): string | null => {
  const normalized = normalizeStringParam(value);
  if (!normalized || isUuid(normalized)) {
    return null;
  }
  return normalized;
};

const normalizeDateOnly = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(trimmed);
  return match ? `${match[1]}-${match[2]}-${match[3]}` : null;
};

const findOrderByOrderNumber = async (orderNumber: string): Promise<Entity | null> => {
  const lookupAttempts: Array<Record<string, string>> = [
    { entity_identifier: `eq.${orderNumber}` },
    { entity_identifier: `ilike.${orderNumber}` },
    { 'entity_data->>document_number': `eq.${orderNumber}` },
    { 'entity_data->>document_number': `ilike.${orderNumber}` },
  ];

  for (const filters of lookupAttempts) {
    const matches = await entityService.list({
      type: 'order',
      limit: 2,
      order: 'entity_identifier.asc',
      filters,
    });

    if (matches.length === 1) {
      return matches[0] ?? null;
    }

    if (matches.length > 1) {
      throw new Error(`Multiple orders found matching "${orderNumber}".`);
    }
  }

  return null;
};

const findOrdersByFilters = async (
  logFormNumber: string | null,
  startDate: string | null,
  endDate: string | null,
): Promise<Entity[]> => {
  const filters: Record<string, string> = {};

  if (logFormNumber) {
    filters['entity_data->>log_form_number'] = `eq.${logFormNumber}`;
  }
  if (startDate) {
    filters['entity_data->>order_date'] = `gte.${startDate}`;
  }
  if (endDate) {
    if (filters['entity_data->>order_date']) {
      filters['entity_data->>order_date'] += `,lte.${endDate}`;
    } else {
      filters['entity_data->>order_date'] = `lte.${endDate}`;
    }
  }

  return entityService.list({
    type: 'order',
    limit: 500,
    order: 'entity_identifier.asc',
    filters,
  });
};

const buildOrderHtml = async (order: Entity): Promise<string> => {
  const orderData = (order.entity_data ?? {}) as Record<string, unknown>;

  const prDescription =
    (orderData.pr_description as string | undefined) ??
    (orderData.description as string | undefined) ??
    null;
  const priority = (orderData.priority as string | undefined) ?? null;
  const assetAuthority = (orderData.asset_authority as string | undefined) ?? null;
  const orderNumber =
    normalizeOrderNumber(order.entity_identifier) ??
    normalizeOrderNumber(orderData.document_number) ??
    `PO-${order.id.slice(0, 8)}`;
  const orderDate = (orderData.order_date as string | undefined) ?? null;
  const expectedDeliveryDate = (orderData.expected_delivery_date as string | undefined) ?? null;
  const purchaseType = (orderData.purchase_type as string | undefined) ?? null;
  const orderStatus = (orderData.status as string | undefined) ?? null;
  const vendor = (orderData.vendor as string | undefined) ?? null;
  const apc = (orderData.apc as string | undefined) ?? null;
  const requiredByDate = (orderData.required_by_date as string | undefined) ?? null;

  const lineItems = await ordersService.getOrderLineItems(order.id);

  const items: AssetAcquisitionOrderItem[] = lineItems.map((row) => {
    const qty = row.entity_data.quantity ?? 0;
    const unit = row.entity_data.unit_price_at_time_of_order ?? null;
    const total = typeof unit === 'number' ? unit * qty : null;
    return {
      lin: row.catalog?.entity_data?.lin ?? null,
      nsn: row.catalog?.entity_data?.nsn ?? null,
      nomenclature: formatNomenclatureWithSize(
        row.catalog?.entity_data?.nomenclature ?? row.catalog?.display_name ?? 'Unknown item',
        row.catalog?.entity_data?.size as string | undefined,
      ),
      quantityOrdered: qty,
      unitCost: typeof unit === 'number' ? unit : null,
      totalLineCost: total,
    };
  });

  const totalCost =
    items.length > 0 ? items.reduce((sum, item) => sum + (item.totalLineCost ?? 0), 0) : null;

  const options: AssetAcquisitionOrderTemplateOptions = {
    generatedAt: new Date(),
    header: {
      prDescription,
      priority,
      assetAuthority,
      orderNumber,
      orderDate,
      expectedDeliveryDate,
      purchaseType,
      orderStatus,
      vendor,
      apc,
      requiredByDate,
      totalCost,
    },
    items,
  };

  return buildAssetAcquisitionOrderHtml(options);
};

export const runAssetAcquisitionOrderReport = async (
  params: Record<string, unknown>,
): Promise<void> => {
  const orderIdParam = normalizeStringParam(params.orderId);
  const orderNumberParam = normalizeStringParam(params.orderNumber);
  const logFormNumber = normalizeStringParam(params.logFormNumber);
  const startDate = normalizeDateOnly(params.startDate);
  const endDate = normalizeDateOnly(params.endDate);

  const lookupValue = orderIdParam ?? orderNumberParam;

  let orders: Entity[] = [];

  if (lookupValue) {
    let order: Entity | null = null;

    if (isUuid(lookupValue)) {
      order = await entityService.get({ type: 'order', id: lookupValue });
    }

    if (!order) {
      order = await findOrderByOrderNumber(lookupValue);
    }

    if (!order) {
      throw new Error(`Order not found for "${lookupValue}".`);
    }

    orders = [order];
  } else if (logFormNumber || startDate || endDate) {
    orders = await findOrdersByFilters(logFormNumber, startDate, endDate);

    if (orders.length === 0) {
      throw new Error('No orders found matching the specified filters.');
    }
  } else {
    throw new Error('Please provide an Order Number, Log Form Number, or date range.');
  }

  const htmlParts = await Promise.all(orders.map(buildOrderHtml));

  const html =
    htmlParts.length === 1
      ? htmlParts[0]!
      : htmlParts.join('\n<div style="page-break-after: always;"></div>\n');

  outputReport(
    html,
    'asset-acquisition-order-form.html',
    getOutputFormat(params),
    getPreOpenedWindow(params),
  );
};
